import React, { useCallback, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'

import bookSelectors from 'store/selectors/booking'
import bookActions from 'store/modules/booking'
import routerSelectors from 'store/selectors/router'
import routerActions from 'store/modules/router'
import bookingActions from 'store/modules/booking'
import networkActions from 'store/modules/network'
import snackbarActions from 'store/modules/snackbar'

import SlotSummary from 'components/book/SlotSummary'
import Window from 'components/dialog/Window'
import Calendar from 'components/book/Calendar'
import BusinessDetails from 'components/book/BusinessDetails'
import Loading from 'components/system/Loading'
import GlobalLoading from 'components/system/GlobalLoading'

import dateUtils from 'shared/src/date'
import formUtils from 'shared/src/formUtils'

import constants from 'shared/src/constants'
import useCalendar from 'components/hooks/usePublicCalendar'

const useStyles = makeStyles(theme => ({
  loading: {
    paddingTop: '100px',
  },
  reserveCell: {
    padding: theme.spacing(1),
  },
}))

const BookingFormCalendar = ({
  
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const classes = useStyles()
  const dispatch = useDispatch()

  const [ clickedSlot, setClickedSlot ] = useState(null)
  const [ clickedSlotTimeoutId, setClickedSlotTimeoutId ] = useState(null)
  const [ recaptchaToken, setRecaptchaToken ] = useState(null)
  const [ recaptchaTokenLoaded, setRecaptchaTokenLoaded ] = useState(false)

  const {
    project,
    bookingform,
  } = useSelector(routerSelectors.params)
  const choosenSlot = useSelector(bookSelectors.slot)
  const config = useSelector(bookSelectors.config)
  const reserveError = useSelector(bookSelectors.reserve.error)

  const onClickSlot = useCallback(async (slot) => {
    if(choosenSlot && choosenSlot.index == slot.index && choosenSlot.date == slot.date) {
      dispatch(bookActions.setSlot(slot))
      dispatch(routerActions.navigateTo('book.form.customize', {
        project,
        bookingform,
      }))
      return
    }
    if(clickedSlotTimeoutId) clearTimeout(clickedSlotTimeoutId)
    dispatch(networkActions.clearError('booking/reserve'))
    setRecaptchaTokenLoaded(false)
    setRecaptchaToken(null)
    setClickedSlot(slot)
    const timeoutId = setTimeout(() => {
      setRecaptchaTokenLoaded(true)
    }, 10 * 1000)
    setClickedSlotTimeoutId(timeoutId)
    try {
      const token = await executeRecaptcha()
      if(token) setRecaptchaToken(token)
      setRecaptchaTokenLoaded(true)
      clearTimeout(timeoutId)
    } catch(e) {
      console.error(`error getting recaptcha token`)
      console.error(e)
    }
  }, [
    choosenSlot,
    project,
    bookingform,
    clickedSlotTimeoutId,
  ])

  const calendar = useCalendar({
    project,
    bookingform,
    config,
  })

  const onHome = useCallback(() => {
    dispatch(routerActions.navigateTo('book.form.start', {
      project,
      bookingform,
    }))
  }, [
    project,
    bookingform,
  ])

  const onCloseSlotWindow = useCallback(() => {
    if(clickedSlotTimeoutId) clearTimeout(clickedSlotTimeoutId)
    setRecaptchaTokenLoaded(false)
    setRecaptchaToken(null)
    setClickedSlot(null)
    dispatch(networkActions.clearError('booking/reserve'))
  }, [
    clickedSlotTimeoutId,
  ])

  const onReserveSlot = useCallback(async () => {
    if(!recaptchaToken) {
      dispatch(snackbarActions.setError(`there was an error verifying the booking`))
      return
    }
    dispatch(bookingActions.reserve({
      project,
      bookingform,
      slot: clickedSlot,
      recaptchaToken,
    }))
  }, [
    project,
    bookingform,
    clickedSlot,
    recaptchaToken,
  ])

  useEffect(() => {
    return () => {
      if(clickedSlotTimeoutId) clearTimeout(clickedSlotTimeoutId)
    }
  }, [])

  useEffect(() => {
    dispatch(bookingActions.loadCalendarConfig({
      project,
      bookingform,
    }))
  }, [
    project,
    bookingform,
  ])

  let windowContent = <Loading />
  let windowTitle = 'Verifying Booking'
  let hasWindowSubmit = false

  if(recaptchaTokenLoaded) {
    if(reserveError || !recaptchaToken) {
      windowTitle = 'Error Verifying Booking'
      const errorText = reserveError.replace(/Error: /g, '')
      windowContent = (
        <>
          <Typography gutterBottom color="error">
            There has been an error verifying this booking slot:<br /><br /> { errorText }<br /><br />
          </Typography>
          <Typography gutterBottom>
            You can still make a booking by clicking "Cancel" and trying another slot or by getting in touch with us:<br /><br />
          </Typography>
          <BusinessDetails
            slotConfig={ config }
          />
        </>
      )
    }
    else {
      hasWindowSubmit = true
      windowTitle = 'Confirm Booking Slot'
      windowContent = (
        <>
          <Typography gutterBottom>
            We will reserve this slot for the next <strong>{ constants.SLOT_TIMEOUT_MINUTES }</strong> minutes.
          </Typography>
          <Typography gutterBottom>
            This will make sure no-one else books this slot in the meantime.
          </Typography>
          <Typography gutterBottom>
            You can always cancel your booking at any point, you will not be charged until you confirm your booking.
          </Typography>
        </>
      )
    }
  }
  
  if(!calendar.isActive) {
    return (
      <div>
        There are no more dates available for this calendar.
      </div>
    )
  }

  return (
    <>
      <Calendar
        calendar={ calendar }
        choosenSlot={ choosenSlot }
        onClickSlot={ onClickSlot }
        onHome={ onHome }
        toolbarButtons={(
          <div>
            <FormControlLabel
              control={<Checkbox checked={ calendar.slotFilter == 'canBook' } onChange={ (e) => calendar.setSlotFilter(e.target.checked ? 'canBook' : 'all') } />}
              label="Hide Fully Booked Slots?"
            />
          </div>
        )}
      />
      {
        clickedSlot && config && (
          <Window
            open
            title={ windowTitle }
            size="md"
            fullWidth
            withCancel
            submitTitle="Proceed"
            onSubmit={ hasWindowSubmit ? onReserveSlot : null }
            onCancel={ onCloseSlotWindow }
          >
            <Grid container spacing={ 0 }>
              <Grid item xs={ 12 } className={ classes.reserveCell }>
                <Typography gutterBottom color="primary" variant="h5">
                  <strong>{ dateUtils.title(clickedSlot.date) }, { formUtils.getTimeRangeTitle(clickedSlot) }</strong>
                </Typography>
              </Grid>
              <Grid item xs={ 12 } sm={ 7 } className={ classes.reserveCell }>
                { windowContent }
              </Grid>
              <Grid item xs={ 12 } sm={ 5 } className={ classes.reserveCell }>
                <SlotSummary
                  slot={ clickedSlot }
                  formConfig={ config }
                />
              </Grid>
            </Grid>
            
          </Window>
        )
      }
      {
        calendar.loading && (
          <GlobalLoading />
        )
      }
    </>
  )
}

const BookingFormCalendarOuter = () => {
  const dispatch = useDispatch()
  const recaptchaKey = useSelector(bookSelectors.recaptchaKey)
  const classes = useStyles()

  useEffect(() => {
    if(recaptchaKey) return
    dispatch(bookingActions.loadRecaptchaKey())
  }, [])

  if(!recaptchaKey) return (
    <div className={ classes.loading }>
      <Loading />
    </div>
  )

  return (
    <GoogleReCaptchaProvider reCaptchaKey={ recaptchaKey }>
      <BookingFormCalendar />
    </GoogleReCaptchaProvider>
  )
}

export default BookingFormCalendarOuter